import { BALANCE } from "./balance";
import { AUDIENCES, C, mechanics, rivals as rivalContent, themes, type AudienceId, type Rival, ngPlus, names } from "@bd/content";
import { WEEKS_PER_YEAR, yearFrac, yearOf, priceIndex } from "./calendar";
import { mechThemeFit } from "./design";
import type { GameState, MarketGame, RivalState, TrendWave } from "./state";
import { clamp, interp, log, newId, rng } from "./util";
import { dexp, dpow } from "./dmath";

// ------------------------------------------------------------------ audiences

/** Weekly potential buyers in an audience this year. */
export function audienceSize(aud: AudienceId, week: number): number {
  return interp(C.audience[aud].size, yearFrac(week));
}

// ------------------------------------------------------------------ trends

export function waveHeat(w: TrendWave, week: number): number {
  if (week < w.start || week > w.end) return 0;
  if (week <= w.peak) return w.strength * ((week - w.start) / Math.max(1, w.peak - w.start));
  return w.strength * (1 - (week - w.peak) / Math.max(1, w.end - w.peak));
}

export function heat(state: GameState, kind: "theme" | "mechanic", target: string, week = state.week): number {
  let h = 0;
  for (const w of state.market.trends) if (w.kind === kind && w.target === target) h += waveHeat(w, week);
  return clamp(h);
}

export function addWave(state: GameState, kind: TrendWave["kind"], target: string, strength: number, lengthWeeks: number, source: string, label: string): TrendWave {
  const start = state.week;
  const w: TrendWave = {
    id: newId(state, "w"), kind, target, start, peak: start + Math.round(lengthWeeks * 0.35), end: start + lengthWeeks,
    strength: clamp(strength), source, label,
  };
  state.market.trends.push(w);
  return w;
}

/** The Trend Board: what's hot right now, hottest first. */
export function trendBoard(state: GameState, week = state.week): { kind: string; target: string; name: string; heat: number; rising: boolean }[] {
  const out = new Map<string, { kind: string; target: string; name: string; heat: number; rising: boolean }>();
  for (const w of state.market.trends) {
    const h = waveHeat(w, week);
    if (h <= 0.02) continue;
    const key = `${w.kind}:${w.target}`;
    const name = w.kind === "theme" ? C.theme[w.target]?.name : C.mechanic[w.target]?.name;
    const prev = out.get(key);
    const rising = week < w.peak;
    if (prev) {
      prev.heat = clamp(prev.heat + h);
      prev.rising ||= rising;
    } else out.set(key, { kind: w.kind, target: w.target, name: name ?? w.target, heat: h, rising });
  }
  return [...out.values()].sort((a, b) => b.heat - a.heat);
}

const WORLD_SPARKS = [
  "A blockbuster film about {x} sparks a craze",
  "A hit TV series puts {x} on every screen",
  "Schools go mad for {x} this year",
  "A bestselling novel makes {x} fashionable",
  "A celebrity can't stop talking about {x}",
];

function worldTrends(state: GameState): void {
  const r = rng(state, "trends");
  const year = yearOf(state.week);
  // about three new world waves a year
  if (!r.chance(3 / WEEKS_PER_YEAR)) return;
  if (r.chance(0.75)) {
    const pool = themes.filter((t) => t.year <= year + 2);
    const t = r.pick(pool);
    const text = r.pick(WORLD_SPARKS).replace("{x}", t.name.toLowerCase());
    addWave(state, "theme", t.id, r.float(0.4, 0.9), r.int(70, 160), "world", text);
    news(state, "trend", `${text}.`);
  } else {
    const pool = mechanics.filter((m) => m.year <= year && !m.hidden);
    const m = r.pick(pool);
    addWave(state, "mechanic", m.id, r.float(0.3, 0.7), r.int(80, 180), "world", `${m.name} games are the talk of the hobby`);
    news(state, "trend", `${m.name} games are the talk of the hobby.`);
  }
}

export function news(state: GameState, kind: string, text: string): void {
  state.market.news.push({ week: state.week, kind, text });
  if (state.market.news.length > 120) state.market.news.splice(0, state.market.news.length - 120);
  log(state, `news:${kind}`, text);
}

// ------------------------------------------------------------------ released games, saturation, copycats

export function addMarketGame(state: GameState, g: Omit<MarketGame, "id" | "sold">): MarketGame {
  const game: MarketGame = { ...g, id: newId(state, "g"), sold: 0 };
  state.market.games.push(game);
  const c = caches.get(state);
  if (c && c.week === state.week && c.n === state.market.games.length - 1) {
    // keep this week's cache in step instead of rebuilding it
    c.n++;
    c.byId.set(game.id, game);
    const age = state.week - game.week;
    if (age < WEEKS_PER_YEAR) c.themeYear.set(game.theme, (c.themeYear.get(game.theme) ?? 0) + 1);
    if (age < 3 * WEEKS_PER_YEAR) c.combo3y.set(`${game.mechanic}|${game.theme}`, (c.combo3y.get(`${game.mechanic}|${game.theme}`) ?? 0) + 1);
    if (age < 2 * WEEKS_PER_YEAR) c.audience2y.set(game.audience, (c.audience2y.get(game.audience) ?? 0) + competitorWeight(game, age));
  }
  state.market.themeCount[g.theme] = (state.market.themeCount[g.theme] ?? 0) + 1;
  const ck = `${g.mechanic}|${g.theme}`;
  state.market.comboCount[ck] = (state.market.comboCount[ck] ?? 0) + 1;
  return game;
}

interface MarketCache {
  week: number;
  n: number;
  byId: Map<string, MarketGame>;
  themeYear: Map<string, number>;
  combo3y: Map<string, number>;
  audience2y: Map<string, number>;
}
let caches = new WeakMap<GameState, MarketCache>();

/** Forgets the market counts (see resetSalesCaches). */
export function resetMarketCache(): void {
  caches = new WeakMap();
}

/** Counts derived from market.games, rebuilt once per week (pure function of the state, never saved). */
function cache(state: GameState): MarketCache {
  let c = caches.get(state);
  if (c && c.week === state.week && c.n === state.market.games.length) return c;
  c = { week: state.week, n: state.market.games.length, byId: new Map(), themeYear: new Map(), combo3y: new Map(), audience2y: new Map() };
  for (const g of state.market.games) {
    c.byId.set(g.id, g);
    const age = state.week - g.week;
    if (age < WEEKS_PER_YEAR) c.themeYear.set(g.theme, (c.themeYear.get(g.theme) ?? 0) + 1);
    if (age < 3 * WEEKS_PER_YEAR) c.combo3y.set(`${g.mechanic}|${g.theme}`, (c.combo3y.get(`${g.mechanic}|${g.theme}`) ?? 0) + 1);
    if (age < 2 * WEEKS_PER_YEAR) c.audience2y.set(g.audience, (c.audience2y.get(g.audience) ?? 0) + competitorWeight(g, age));
  }
  caches.set(state, c);
  return c;
}

export function marketGame(state: GameState, id: string): MarketGame | undefined {
  return cache(state).byId.get(id);
}

/** Games in this theme released in the last year (all publishers). */
export function themeReleasesLastYear(state: GameState, theme: string, excludeId?: string): number {
  const c = cache(state);
  let n = c.themeYear.get(theme) ?? 0;
  const ex = excludeId ? c.byId.get(excludeId) : undefined;
  if (ex && ex.theme === theme && state.week - ex.week < WEEKS_PER_YEAR) n--;
  return n;
}

export function saturation(state: GameState, theme: string, excludeId?: string): number {
  return clamp((themeReleasesLastYear(state, theme, excludeId) - 3) * 0.05, 0, 0.4);
}

/** Same mechanic x theme games in the last three years. */
export function comboCopies(state: GameState, mechanic: string, theme: string, excludeId?: string): number {
  const c = cache(state);
  let n = c.combo3y.get(`${mechanic}|${theme}`) ?? 0;
  const ex = excludeId ? c.byId.get(excludeId) : undefined;
  if (ex && ex.mechanic === mechanic && ex.theme === theme && state.week - ex.week < 3 * WEEKS_PER_YEAR) n--;
  return n;
}

/** Good, recent games crowd an audience; old or weak ones barely do. */
function competitorWeight(g: MarketGame, age: number): number {
  return dpow(clamp(g.score / 10), 3) * dexp(-age / 26);
}

/** Competition for an audience: weighted count of recent releases (see competitorWeight). */
export function audienceCrowding(state: GameState, aud: AudienceId): number {
  return cache(state).audience2y.get(aud) ?? 0;
}

/** A hit makes waves: its theme heats up and copycat rivals queue up their own versions. */
export function onHit(state: GameState, g: MarketGame, publisherName: string): void {
  if (g.score < 8) return;
  const r = rng(state, "copycats");
  addWave(state, "theme", g.theme, clamp(0.35 + (g.score - 8) * 0.3), r.int(60, 120), g.publisher, `${publisherName}'s ${g.name} makes ${C.theme[g.theme].name.toLowerCase()} hot`);
  // online: a cousin's hit shows up in everyone's world
  if (state.companies.length > 1 && state.companies.some((c) => c.id === g.publisher)) {
    log(state, "news:cousin", `${publisherName}'s ${g.name} started a ${C.theme[g.theme].name.toLowerCase()} craze.`);
  }
  for (const rs of state.rivals) {
    if (rs.bust || rs.id === g.publisher) continue;
    const rc = C.rival[rs.id];
    if (!r.chance(rc.copycat)) continue;
    rs.copyQueue.push({ mechanic: g.mechanic, theme: g.theme, audience: g.audience, week: state.week + r.int(16, 52), of: g.id });
  }
}

// ------------------------------------------------------------------ rivals

export function rivalActive(rs: RivalState, rc: Rival, week: number): boolean {
  if (rc.upstart) return !rs.bust && rs.foundedWeek !== undefined && week >= rs.foundedWeek;
  return !rs.bust && (!rc.from || yearOf(week) >= rc.from);
}

/** Make sure generated publishers are known to this thread's content tables (after a load, or in the UI). */
export function registerRivals(state: GameState): void {
  for (const rs of state.rivals) if (rs.gen && !C.rival[rs.id]) (C.rival as Record<string, Rival>)[rs.id] = rs.gen;
}

/** A brand-new publisher: a name from the word lists, the style of one of the originals, its own colour. */
function generateRival(state: GameState): RivalState {
  const r = rng(state, "gen-rival");
  const n = state.rivals.filter((x) => x.gen).length + 1;
  const template = r.pick(rivalContent.filter((x) => !x.acquirer));
  const word = r.pick(names.adjectives);
  const noun = r.pick(["Table", "Meeple", "Cardboard", "Lantern", "Harbour", "Orchard", "Comet", "Thistle", "Anvil", "Kettle", "Sparrow", "Quarry"]);
  const tail = r.pick(["Games", "Play", "Studio", "& Co.", "Works", "House"]);
  const hue = Math.floor(r.float(0, 360));
  const color = `#${[0, 8, 4].map((k) => { const v = Math.round(255 * (0.55 - 0.3 * Math.max(-1, Math.min(1, Math.min(((k + hue / 30) % 12) - 3, 9 - ((k + hue / 30) % 12)))))); return v.toString(16).padStart(2, "0"); }).join("")}`;
  const def: Rival = {
    ...template, id: `gen_${n}`, name: `${word} ${noun} ${tail}`, upstart: true, from: undefined,
    quality: Math.round((template.quality + r.float(-0.3, 0.4)) * 10) / 10, cash: Math.round(template.cash * r.float(0.7, 1.4)), color,
    personality: template.personality, will: template.will,
  };
  const rs: RivalState = { id: def.id, cash: 0, reputation: 40, nextRelease: state.week + 8, released: 0, bust: false, staffPoached: 0, copyQueue: [], gen: def };
  state.rivals.push(rs);
  (C.rival as Record<string, Rival>)[def.id] = def;
  return rs;
}

/** When buyouts and bankruptcies thin the market below six, a newcomer opens each year: the field never empties. */
function newcomers(state: GameState): void {
  if (state.week % WEEKS_PER_YEAR !== 0 || yearOf(state.week) < 1975) return;
  const active = state.rivals.filter((rs) => rivalActive(rs, C.rival[rs.id], state.week)).length;
  if (active >= 6) return;
  const lastFounded = Math.max(-999, ...state.rivals.map((rs) => rs.foundedWeek ?? -999));
  // the field never empties: while it's thin, a newcomer opens every year
  if (state.week - lastFounded < WEEKS_PER_YEAR) return;
  let next = state.rivals.find((rs) => C.rival[rs.id]?.upstart && rs.foundedWeek === undefined);
  // the list of newcomers ran out: someone new starts up anyway
  if (!next) next = generateRival(state);
  const rc = C.rival[next.id];
  next.foundedWeek = state.week;
  next.cash = rc.cash * priceIndex(state.week);
  next.reputation = 40;
  next.nextRelease = state.week + 8;
  news(state, "rival", `A new publisher opens: ${rc.name}. ${rc.personality}.`);
}

function rivalRelease(state: GameState, rs: RivalState, rc: Rival): void {
  const r = rng(state, `rival:${rs.id}`);
  const year = yearOf(state.week);
  const due = rs.copyQueue.findIndex((q) => q.week <= state.week);
  let mechanic: string;
  let theme: string;
  let audience: AudienceId;
  let copyOf: string | undefined;
  if (due >= 0) {
    const q = rs.copyQueue.splice(due, 1)[0];
    ({ mechanic, theme, audience } = q);
    copyOf = q.of;
  } else {
    const mechs = rc.mechanics.filter((m) => C.mechanic[m].year <= year);
    const pool = mechs.length ? mechs : mechanics.filter((m) => m.start).map((m) => m.id);
    const auds = rc.audiences.filter((a) => C.audience[a].year <= year);
    audience = auds.length ? r.pick(auds) : "family";
    // rivals chase what's hot a little
    const hot = trendBoard(state).filter((t) => t.kind === "theme").slice(0, 3);
    theme = hot.length && r.chance(0.3) ? r.pick(hot).target : r.pick(rc.themes.filter((t) => C.theme[t].year <= year + 2));
    mechanic = r.weighted(pool, (m) => mechThemeFit(m, theme));
  }
  const fit = mechThemeFit(mechanic, theme);
  const eraSkill = clamp((year - 1965) / 70) * 0.8;
  const score = clamp(r.normal(rc.quality + eraSkill + (fit - 3) * 0.35 - (copyOf ? 0.3 : 0), rc.spread), 1, 9.8);
  const name = rivalGameName(r, theme, mechanic);
  const g = addMarketGame(state, { publisher: rs.id, name, mechanic, theme, audience, format: C.mechanic[mechanic].formats[0], week: state.week, score: Math.round(score * 10) / 10, copyOf });
  rs.released += 1;
  // rival economics: good games make money, bad ones cost
  const scale = 4000 * dpow(1.028, year - 1965);
  rs.cash += scale * (score - 5.2) * (rc.acquirer ? 2 : 1);
  rs.reputation = clamp(rs.reputation * 0.85 + score * 10 * 0.15, 0, 100);
  if (score >= 8) {
    news(state, "rival", `${rc.name} releases ${name}, and critics love it (${g.score.toFixed(1)}).`);
    onHit(state, g, rc.name);
  } else if (copyOf) {
    const orig = state.market.games.find((x) => x.id === copyOf);
    if (orig && !C.rival[orig.publisher]) news(state, "rival", `${rc.name} rushes out ${name}, a copy of ${orig.name}.`);
  }
  rs.nextRelease = state.week + Math.max(3, Math.round(rc.cadence * BALANCE.rivalPace * (state.settings.newGamePlus ? ngPlus.rivalCadence : 1) * r.float(0.7, 1.3)));
}

const NAME_A = ["Grand", "Little", "Lucky", "Wild", "Secret", "Midnight", "Royal", "Tiny", "Mega", "Golden", "Crooked", "Silver", "Brave", "Quick", "Hidden"];
const NAME_B: Record<string, string[]> = {
  default: ["Quest", "Party", "Rush", "Club", "Gambit", "Kingdom", "League", "Dash", "Saga", "Bazaar", "Expedition", "Showdown", "Parade", "Heist", "Harvest"],
};

function rivalGameName(r: ReturnType<typeof rng>, theme: string, _mechanic: string): string {
  const t = C.theme[theme].name.split(/[ &']/)[0];
  return r.chance(0.5) ? `${r.pick(NAME_A)} ${t} ${r.pick(NAME_B.default)}` : `${t} ${r.pick(NAME_B.default)}`;
}

function rivalsWeek(state: GameState): void {
  newcomers(state);
  for (const rs of state.rivals) {
    const rc = C.rival[rs.id];
    if (!rivalActive(rs, rc, state.week)) continue;
    if (state.week >= rs.nextRelease) rivalRelease(state, rs, rc);
    // overheads
    rs.cash -= 260 * dpow(1.028, yearOf(state.week) - 1965) * (rc.acquirer ? 3 : 1);
    if (rs.cash < -40000 * dpow(1.028, yearOf(state.week) - 1965)) {
      rs.bust = true;
      rs.bustWeek = state.week;
      rs.catalogueFor = Math.round(rs.released * 900 * dpow(1.028, yearOf(state.week) - 1965));
      news(state, "rival", `${rc.name} goes bust. Its back catalogue of ${rs.released} games is up for auction.`);
    }
  }
}

function pruneMarket(state: GameState): void {
  // keep six years of releases in detail; older ones only count in the archive
  const cutoff = state.week - 6 * WEEKS_PER_YEAR;
  if (state.market.games.length && state.market.games[0].week < cutoff) {
    const keep = state.market.games.filter((g) => g.week >= cutoff);
    state.market.archived += state.market.games.length - keep.length;
    state.market.games = keep;
  }
  state.market.trends = state.market.trends.filter((w) => w.end >= state.week);
  if (state.market.shortage && state.market.shortage.until <= state.week) {
    news(state, "industry", state.market.shortage.kind === "paper" ? "The card-stock shortage is over." : "The import tariff is lifted.");
    state.market.shortage = null;
  }
}

function industryShocks(state: GameState): void {
  const r = rng(state, "industry");
  if (state.market.shortage || !r.chance(1 / (WEEKS_PER_YEAR * 6))) return;
  const kind = r.chance(0.6) ? "paper" : "tariff";
  const mult = kind === "paper" ? r.float(1.15, 1.35) : r.float(1.1, 1.25);
  state.market.shortage = { kind, until: state.week + r.int(20, 52), costMult: mult };
  news(state, "industry", kind === "paper" ? `A card-stock shortage hits every printer: costs up ${Math.round((mult - 1) * 100)}%.` : `A new import tariff hits every importer: costs up ${Math.round((mult - 1) * 100)}%.`);
}

export const marketSystem = {
  name: "market",
  init(state: GameState) {
    // the 1965 market already has a few waves in motion
    const r = rng(state, "trends");
    for (let i = 0; i < 3; i++) {
      const t = r.pick(themes.filter((x) => x.start));
      const w = addWave(state, "theme", t.id, r.float(0.3, 0.7), r.int(60, 120), "world", `${t.name} are in fashion`);
      w.start -= r.int(10, 40);
    }
  },
  week(state: GameState) {
    worldTrends(state);
    industryShocks(state);
    rivalsWeek(state);
    if (state.week % 13 === 0) pruneMarket(state);
  },
};

export function rivalNames(): string[] {
  return rivalContent.map((r) => r.name);
}

export function allAudiences(): AudienceId[] {
  return AUDIENCES;
}
